import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PhotoIcon, VideoCameraIcon, TrashIcon } from "@heroicons/react/24/outline";
import Pagination from "../../../components/common/Pagination";
import LoadingSpinner from "../../../components/common/LoadingSpinner";
import DeleteConfirmation from "../../../components/common/DeleteConfirmation";
import { productRequestService } from "../services/productRequestService";

const dateTime = (d) =>
  d
    ? new Date(d).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "–";

const ProductRequestList = () => {
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["product-requests", page],
    queryFn: () => productRequestService.getProductRequests({ page }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => productRequestService.deleteProductRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["product-requests"]);
      setDeleteTarget(null);
    },
  });

  if (isLoading) return <LoadingSpinner />;
  if (isError)
    return (
      <div className="p-4 text-sm text-red-500">
        Erreur lors du chargement des recherches produit.
      </div>
    );

  const requests = data?.data || [];
  const pagination = data?.meta || {};

  return (
    <div className="bg-white min-h-full shadow-md rounded-lg">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800">Produits recherchés</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Recherches ouvertes par les clients — les boutiquiers les voient et contactent
          directement le demandeur. Disparaît de cette liste dès que le client marque sa
          recherche trouvée.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-primary text-white">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Pièce jointe</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Description</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Demandeur</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Téléphone</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Date</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase">Actions</th>
            </tr>
          </thead>
          {requests.length > 0 ? (
            <tbody className="bg-white divide-y divide-gray-200">
              {requests.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-3">
                    {r.media_type === "image" ? (
                      <a href={r.media_url} target="_blank" rel="noreferrer">
                        <img
                          src={r.media_url}
                          alt=""
                          className="h-12 w-12 rounded-md object-cover"
                        />
                      </a>
                    ) : (
                      <a
                        href={r.media_url}
                        target="_blank"
                        rel="noreferrer"
                        title="Voir la vidéo"
                        className="h-12 w-12 rounded-md bg-gray-100 flex items-center justify-center"
                      >
                        <VideoCameraIcon className="h-6 w-6 text-gray-400" />
                      </a>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 max-w-xs">
                    <div className="line-clamp-2">{r.description}</div>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-800">
                    {r.user?.firstname} {r.user?.lastname}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{r.user?.phone_number}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{dateTime(r.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      title="Supprimer"
                      className="p-2 hover:bg-red-100 rounded cursor-pointer"
                      onClick={() => setDeleteTarget(r)}
                    >
                      <TrashIcon className="h-5 w-5 text-red-500" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          ) : (
            <tbody>
              <tr>
                <td colSpan="6">
                  <div className="flex flex-col items-center justify-center py-10 text-gray-400 text-center text-sm gap-2">
                    <PhotoIcon className="h-8 w-8" />
                    Aucune recherche produit ouverte pour le moment.
                  </div>
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>

      <Pagination
        currentPage={page}
        totalItems={pagination.total}
        itemsPerPage={pagination.per_page}
        onPageChange={setPage}
      />

      <DeleteConfirmation
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
        title="Supprimer cette recherche"
        message={`Supprimer définitivement la recherche de ${
          deleteTarget?.user?.firstname ?? ""
        } ${deleteTarget?.user?.lastname ?? ""} ? La pièce jointe sera aussi supprimée. Cette action est irréversible.`}
        confirmButtonText="Supprimer"
        confirmButtonColor="danger"
      />
    </div>
  );
};

export default ProductRequestList;
